import {
  ContingencyPlanType,
  AssessmentTeamCreate,
  AssessmentTeamResponse,
  FileProps,
  Log,
  OptionsEnum,
  RiskLevelEnum,
  SceStatus,
  User,
} from "@shape/reef-shared";
import type { RecommendationResponse as SharedRecommendationResponse } from "@shape/reef-shared";

export type RecommendationCreate = {
  user_id: number | null;
  due_date: string | null;
  recommendation: string | null;
  permanent_change: boolean | null;
  additional_mitigation_needed?: boolean | null;
  potential_consequences_changed?: boolean | null;
  has_action_to_be_closed?: boolean | null;
  temporary_due_date?: string | null;
  tags?: string[] | null;
  tag_codes?: string[] | null;
};

export type Step2RecommendationResponse = SharedRecommendationResponse & {
  tag_codes?: string[] | null;
};

export enum RiskAssessmentStatus {
  WAITING_REVIEW = "WAITING_REVIEW",
  MORE_INFORMATION_REQUIRED = "MORE_INFORMATION_REQUIRED",
  COMPLEMENT_PENDING = "COMPLEMENT_PENDING",
  COMPLETED = "COMPLETED",
}

export interface RiskAssessmentDecision {
  decision: 0 | 1;
  justification?: string | null;
  user: User | null;
}

export interface RiskAssessmentDecisionBody {
  decision_type: "RISK_ASSESSMENT_REVIEW";
  decision: 0 | 1;
  justification?: string;
}

export interface AffectedAreaMahResponse {
  mah_code: string;
  mah_name: string;
}

export interface AffectedAreaTagSummaryResponse {
  tag_code: string;
  mahs: AffectedAreaMahResponse[];
  has_available_options: boolean;
}

export interface AffectedAreaRowResponse {
  row_id: string;
  tag_code: string;
  mah_code: string;
  barrier_code: string;
  barrier_name: string;
  origin_code: string;
  origin_name: string;
}

export interface AffectedAreaAccordionResponse {
  tag_code: string;
  mah: AffectedAreaMahResponse;
  rows: AffectedAreaRowResponse[];
}

export interface AffectedAreaSelectionResponse {
  tag_code: string;
  mah_code: string;
  barrier_code: string;
  origin_code: string;
}

export type AffectedAreaSelectionFormValue = AffectedAreaSelectionResponse;

export interface Step2Props {
  external_element_impacted?: boolean | null;
  has_sis_modification?: boolean | null;
  sis_modification_justification?: string | null;
  sis_modification_justification_files?: FileProps[];
  potential_consequences_affected: string | null;
  contingency_plan?: ContingencyPlanType | null;
  risk_level: RiskLevelEnum | null;
  risk_level_justification: string | null;
  risk_level_files: FileProps[] | undefined;
  assigning_recommendation_files?: FileProps[];
  mopo_reviewed: boolean | null | string;
  is_issue_managed_in_other_system?: boolean | null | string;
  issue_id_in_other_system?: string | null;
  risk_level_after_measures: RiskLevelEnum | null;
  risk_level_after_measures_comment: string | null;
  risk_level_after_measures_files?: FileProps[];
  affected_areas?: AffectedAreaSelectionResponse[] | null;
  approvals?:
    | {
        approved: boolean;
        comments: string;
        user: User | null;
      }[]
    | null;
  step_approved_by: User | null;
  step_approved_at: Date | null;
  draft: boolean;
}

export interface Step2Response extends Step2Props, Log {
  cto_code: string;
  assessment_team?: AssessmentTeamResponse[];
  assigning_recommendations: Step2RecommendationResponse[];
  affected_area_tags?: AffectedAreaTagSummaryResponse[] | null;
  risk_assessment_submitted_by?: User | null;
  risk_assessment_status?: RiskAssessmentStatus | null;
  risk_assessment_decisions?: RiskAssessmentDecision[] | null;
  sces: Omit<SceStatus, "expected_function" | "performance_standards">[];
  can_user_fill_step: boolean;
  eligible_users?: string[];
}

export interface CreateStep2Body extends Step2Props {
  assigning_recommendations: RecommendationCreate[];
  has_contingency_plan?: OptionsEnum;
}

export interface CreateStep2MdbBody extends CreateStep2Body {
  assessment_team: AssessmentTeamCreate[];
}

export type CreateStep2RiskAssessmentComplementBody = Pick<
  CreateStep2Body,
  | "risk_level"
  | "risk_level_justification"
  | "risk_level_files"
  | "risk_level_after_measures"
  | "risk_level_after_measures_comment"
  | "risk_level_after_measures_files"
  | "assigning_recommendations"
  | "draft"
>;

export interface CreateStep2MdbFormValues extends CreateStep2Body {
  assessment_team?: AssessmentTeamCreate[];
  assessment_team_plus?: (string | number)[];
  affected_areas?: AffectedAreaSelectionFormValue[] | null;
}
